import React from 'react';
import { Globe } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useCurrency, Currency } from '../context/CurrencyContext';
import { cn } from '@/lib/utils';

interface CurrencySelectorProps {
  className?: string;
  variant?: 'default' | 'compact';
}

const CURRENCY_OPTIONS = [
  {
    code: 'USD' as Currency,
    name: 'US Dollar',
    symbol: '$',
    flag: '🇺🇸'
  },
  {
    code: 'CAD' as Currency,
    name: 'Canadian Dollar',
    symbol: '$',
    flag: '🇨🇦'
  },
  {
    code: 'GBP' as Currency,
    name: 'British Pound',
    symbol: '£',
    flag: '🇬🇧'
  }
];

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({ 
  className,
  variant = 'default'
}) => {
  const { selectedCurrency, setCurrency, isLoading } = useCurrency();

  const selectedOption = CURRENCY_OPTIONS.find(option => option.code === selectedCurrency);

  const handleCurrencyChange = (value: string) => {
    setCurrency(value as Currency);
  };

  if (variant === 'compact') {
    return (
      <Select 
        value={selectedCurrency} 
        onValueChange={handleCurrencyChange}
        disabled={isLoading}
      >
        <SelectTrigger className={cn("w-20 h-8", className)}>
          <SelectValue>
            <span className="text-sm font-medium">
              {selectedOption?.symbol}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="z-[2147483647]">
          {CURRENCY_OPTIONS.map((option) => (
            <SelectItem key={option.code} value={option.code}>
              <div className="flex items-center gap-2">
                <span>{option.flag}</span>
                <span className="font-medium">{option.symbol}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Globe className="h-4 w-4 text-jamaica-green" />
      <Select 
        value={selectedCurrency} 
        onValueChange={handleCurrencyChange}
        disabled={isLoading}
      >
        <SelectTrigger className="w-40">
          <SelectValue>
            {selectedOption && (
              <div className="flex items-center gap-2">
                <span>{selectedOption.flag}</span>
                <span className="font-medium">{selectedOption.code}</span>
                <span className="text-muted-foreground">({selectedOption.symbol})</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="z-[2147483647]">
          {CURRENCY_OPTIONS.map((option) => (
            <SelectItem key={option.code} value={option.code}>
              <div className="flex items-center gap-2">
                <span>{option.flag}</span>
                <span className="font-medium">{option.code}</span>
                <span className="text-muted-foreground">- {option.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default CurrencySelector;